/**
 * Konu 12 — salt-tarih (date-only) değerleri için timezone-güvenli yardımcılar.
 *
 * Backend `titles.release_date`, `episodes.release_date`,
 * `seasons.release_date`, `people.birth_date` ve `people.death_date` alanlarını
 * `2005-08-29` biçiminde, saat/offset bilgisi OLMADAN gönderir. Bu değerler
 * takvim günüdür; bir zaman anı değildir. `parseAbsolute()` böyle bir string'i
 * kabul etmediği (ve `new Date('2005-08-29')` UTC gece yarısı varsayıp yerel
 * saate göre gün kaydırdığı) için burada UTC gece yarısına sabitlenip UTC
 * olarak biçimlendirilirler → ziyaretçi hangi saat diliminde olursa olsun
 * aynı gün görünür.
 */

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isDateOnlyString(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY_PATTERN.test(value.trim());
}

/**
 * `2005-08-29` → `2005-08-29T00:00:00.000Z`
 *
 * Geriye dönük uyumluluk: backend'in eski davranışıyla üretilmiş tam ISO
 * string'ler (`2005-08-28T21:00:00.000000Z`) bu fonksiyona hiç girmez,
 * `isDateOnlyString` onları eşleştirmez ve mevcut yol işlemeye devam eder.
 * Deploy anında SSR/API cache'inde kalan eski değerler bu sayede kırılmaz.
 */
export function dateOnlyToUtcIso(value: string): string {
  return `${value.trim()}T00:00:00.000Z`;
}

/**
 * Salt-tarih string'ini ziyaretçinin YEREL gece yarısına kurar.
 *
 * "Vizyona kaç gün kaldı" gibi ziyaretçinin kendi takvimine göre anlam kazanan
 * hesaplar için kullanılır: `new Date('2026-11-26')` UTC gece yarısı olduğu
 * için UTC'nin gerisindeki saat dilimlerinde bir önceki güne düşer ve geri
 * sayım 1 gün kayar. Tam ISO string'ler doğal biçimde ayrıştırılır.
 */
export function toDisplayDate(value?: string | null): Date | null {
  if (!value) return null;
  const parts = dateOnlyParts(value);
  if (parts && isDateOnlyString(value)) {
    return new Date(parts.year, parts.month - 1, parts.day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Salt-tarih string'inin yıl/ay/gün parçalarını `Date` kurmadan okur.
 * Yıl türetme ve yaş hesabı gibi yerlerde saat dilimi etkisini tamamen
 * devre dışı bırakmak için kullanılır.
 */
export function dateOnlyParts(
  value: unknown,
): {year: number; month: number; day: number} | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}
